import clsx from "clsx";
import { enqueueSnackbar } from "notistack";
import { useEffect, useRef, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import { useTeam } from "~/contexts/TeamProvider.tsx";
import { canMember } from "~/helpers/authorizations";

import { Team, TeamAttributes } from "../../../api/Api";
import Button from "../../mantine/button/Button";
import styles from "./UploadImage.module.scss";

const ALLOWED_FILE_TYPES = [
  "image/png",
  "image/gif",
  "image/jpg",
  "image/jpeg",
  "image/webp",
];
const MAX_FILE_SIZE = 5 * 1024 * 1024;

type UploadImageProps = {
  currentTeam: ((TeamAttributes | Team) & { id: number }) | null;
  handleDeleteLogo: () => void;
  /** Shown instead of the letter when there is no logo. */
  placeholderImageUrl?: string;
};

const UploadImage = ({
  currentTeam,
  handleDeleteLogo,
  placeholderImageUrl,
}: UploadImageProps) => {
  const { authorizations: teamAuthorizations } = useTeam();
  const canUpdateTeam = canMember(teamAuthorizations, "update");
  const { register, setValue, control } = useFormContext();
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [displayImage, setDisplayImage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const logoFile = useWatch({
    control,
    name: "logoFile",
    defaultValue: undefined,
  });

  useEffect(() => {
    if (logoFile === undefined || logoFile === null) {
      setSelectedImage(null);
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
      }

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  }, [logoFile, previewUrl]);

  const getLogoUrl = () => {
    if (!currentTeam?.logo) return null;

    if (typeof currentTeam.logo === "object" && currentTeam.logo.url) {
      return currentTeam.logo.url;
    }

    return typeof currentTeam.logo === "string" ? currentTeam.logo : null;
  };

  const logoUrl = getLogoUrl();

  useEffect(() => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setDisplayImage(logoUrl || null);
    if (selectedImage) {
      const objectUrl = URL.createObjectURL(selectedImage);
      setPreviewUrl(objectUrl);
      setDisplayImage(objectUrl);

      return () => {
        URL.revokeObjectURL(objectUrl);
      };
    }
  }, [selectedImage]);

  const validateFile = (file: File): boolean => {
    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      enqueueSnackbar(
        "Invalid file type. Please upload PNG, GIF, JPG, JPEG, or WebP images only.",
        { variant: "error", persist: true },
      );

      return false;
    }

    if (file.size > MAX_FILE_SIZE) {
      enqueueSnackbar(
        "File size exceeds 5MB limit. Please upload a smaller image.",
        { variant: "error", persist: true },
      );

      return false;
    }

    return true;
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (validateFile(file)) {
        setValue("logoFile", file, {
          shouldDirty: true,
          shouldTouch: true,
        });
        setSelectedImage(file);
      } else {
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        setValue("logoFile", undefined);
      }
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleDeleteImage = () => {
    setDisplayImage(null);
    setSelectedImage(null);
    handleDeleteLogo();
    setValue("logo", undefined);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className={styles.settingsLogoUpload}>
      <div
        className={clsx({
          [styles.settingsLogoPreviewEmpty]: displayImage,
          [styles.settingsLogoPreview]: !displayImage,
          [styles.settingsLogoPreviewDisabled]: !canUpdateTeam,
        })}
        onClick={canUpdateTeam ? handleUploadClick : undefined}
        role={canUpdateTeam ? "button" : undefined}
        tabIndex={canUpdateTeam ? 0 : undefined}
        onKeyDown={
          canUpdateTeam
            ? (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  handleUploadClick();
                }
              }
            : undefined
        }
        aria-label="Upload Logo"
        aria-disabled={!canUpdateTeam}
      >
        {displayImage ? (
          <div className={styles.settingsImageContainer}>
            <img
              src={displayImage}
              alt="Team logo"
              className={styles.settingsUploadedImage}
            />
          </div>
        ) : placeholderImageUrl ? (
          <div className={styles.settingsImageContainer}>
            <img
              src={placeholderImageUrl}
              alt=""
              className={styles.settingsUploadedImage}
            />
          </div>
        ) : (
          <span className={styles.settingsLogoPlaceholder}>
            {currentTeam?.name?.[0]?.toUpperCase() || "T"}
          </span>
        )}
        <div
          className={clsx(
            styles.settingsUuploadIcon,
            !canUpdateTeam && styles.uploadIconDisabled,
          )}
        >
          <i className={styles.settingsPhotoIcon} aria-hidden="true" />
        </div>
      </div>
      {displayImage && (
        <Button
          onClick={handleDeleteImage}
          className={styles.settingsDeleteButton}
          type="button"
          variant="danger-outline"
        >
          Delete Team Logo
        </Button>
      )}
      <input
        disabled={!canUpdateTeam}
        {...register("logoFile")}
        ref={(e) => {
          if (e) {
            register("logoFile").ref(e);
            (
              fileInputRef as React.MutableRefObject<HTMLInputElement | null>
            ).current = e;
          }
        }}
        type="file"
        accept={ALLOWED_FILE_TYPES.join(",")}
        onChange={handleImageUpload}
        aria-label="Upload team logo"
        hidden
      />
    </div>
  );
};

export default UploadImage;
